import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Card, CardContent, Typography, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import Check from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import PremiumIcon from '../../assets/svgs/premiumIcon.svg';
import { BASE_URL } from "../../constants/Constants";
import axiosInstance from "../../services/axiosInstance";
import LoginPopup from "../Login/LoginPopup";
import { fetchSubscriptionPlans } from "../../services/razorpayService";
import "./SubscriptionPlans.css";

function getRazorpayPlanId(sub) {
  return sub?.razorpay_plan_id ?? sub?.subscription_plan?.razorpay_plan_id;
}

function getSubscriptionStatus(sub) {
  return sub?.status ?? sub?.subscription_status?.code ?? "";
}

/** Mirrors API `hasActiveFreeSubscription`: free plan still valid for access. */
function isActiveFreeSubscription(row, now = new Date()) {
  const planId = getRazorpayPlanId(row);
  const status = getSubscriptionStatus(row).toLowerCase();
  if (!row || planId !== "free" || !row.end_at) return false;
  if (new Date(row.end_at) <= now) return false;
  return ["active", "completed"].includes(status);
}

function formatValidUntilDate(raw) {
  if (raw == null || raw === "") return "";
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

}

const isSubscriptionActive = (subscription) => {
  if (!subscription) return false;

  const status = getSubscriptionStatus(subscription)?.toLowerCase();

  if (status !== "active" && status !== "completed") {
    return false;
  }

  if (!subscription.end_at) return false;

  const now = new Date();
  const endDate = new Date(subscription.end_at);
  endDate.setHours(23, 59, 59, 999);
  return endDate > now; 
};

export const isPlanStillActive = (endDate, status) => {
  if (!endDate) return false;
  if (status !== "active" && status !== "completed") {
    return false;
  }
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  return end >= new Date();
};

export default function SubscriptionPlans() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [freePlanUsed, setFreePlanUsed] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);
  const [activatingFree, setActivatingFree] = useState(false);
  const [freeSuccessOpen, setFreeSuccessOpen] = useState(false);
  const [freeValidTill, setFreeValidTill] = useState("");
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [hasAuthenticatedSubscription, setHasAuthenticatedSubscription] = useState(false);
  const [plans, setPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const userId = localStorage.getItem('user_id')
  const navigate = useNavigate();

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const data = await fetchSubscriptionPlans();
        setPlans(data);
      } catch (err) {
        console.error("Error fetching plans", err);
      }
    };

    loadPlans();
  }, []);

 useEffect(() => {
    if (!userId || !plans.length) {
      setFreePlanUsed(false);
      setCurrentPlanId(null);
      return;
    }

    async function fetchUserSubscription() {
      try {
        const res = await axiosInstance.get(`${BASE_URL}/razorpay/subscription/${userId}`);
        const data = res.data;

        const userSubscription = data?.subscription;
        const freeUsed = !!userSubscription;
        setFreePlanUsed(freeUsed);
        const planId = getRazorpayPlanId(userSubscription);
        /** Disable free card: user has a paid plan, or free tier period ended. */
        const hasPaidPlan = !!planId && planId !== "free";
        const freeTierInactive =
          planId === "free" && !isActiveFreeSubscription(userSubscription);
        const expired = hasPaidPlan || freeTierInactive;
        if (expired) {
          setSelectedPlan((prev) => (prev?.id === "free" ? null : prev));
        }
        if (userSubscription) {
          const status = getSubscriptionStatus(userSubscription).toLowerCase();
          // Check if there's an authenticated (pending upgrade) subscription
          setHasAuthenticatedSubscription(data.authenticatedExists);
          if (isSubscriptionActive(userSubscription)) {
          const plan = plans.find((p) => p.id === getRazorpayPlanId(userSubscription));
          if (plan) {
            setSelectedPlan(plan);
            setCurrentPlanId(getRazorpayPlanId(userSubscription));
            setSubscriptionEndDate(userSubscription?.end_at);
            setSubscriptionStatus(getSubscriptionStatus(userSubscription));
            }
          } else {
            setSubscriptionStatus("expired");
            setSelectedPlan(null);
            setCurrentPlanId(null);
          }
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }
    }
    fetchUserSubscription();
  }, [userId , plans]);

  // After login from "Login/Signup to Subscribe", restore the plan they picked (stay on /subscribe).
  useEffect(() => {
    if (!userId) return;
    const raw = sessionStorage.getItem('pendingPlan');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const full = plans.find((p) => p.id === parsed?.id);
      if (full) setSelectedPlan(full);
    } catch {
      // ignore invalid JSON
    } finally {
      sessionStorage.removeItem('pendingPlan');
    }
  }, [userId, plans]);

  const formatFreeValidTill = (payload) => {
    const p = payload?.data && typeof payload.data === "object" ? payload.data : payload;
    const raw =
      p?.subscription?.end_at ??
      p?.subscription?.endAt ??
      p?.end_at ??
      p?.endAt;
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    }
    const fallback = new Date();
    fallback.setFullYear(fallback.getFullYear() + 1);
    return fallback.toLocaleDateString();
  };

  const handleNext = async () => {
    if (!userId) {
      sessionStorage.setItem('pendingPlan', JSON.stringify(selectedPlan));
      setOpenLogin(true);
      return;
    }
    if (selectedPlan?.id === "free" && !freePlanUsed) {
      setActivatingFree(true);
      try {
        const res = await axiosInstance.post(
          `${BASE_URL}/razorpay/activate-free-subscription`,
          { user_id: userId },
          { skipErrorToast: true }
        );
        if (res.data?.success) {
          const validTill = formatFreeValidTill(res.data);
          setFreeValidTill(validTill);
          setSubscriptionEndDate(validTill);
          setSubscriptionStatus("active");
          const freePlan = plans.find((p) => p.id === "free");
          if (freePlan) setSelectedPlan(freePlan);
          setFreePlanUsed(true);
          setCurrentPlanId("free");
          setFreeSuccessOpen(true);
        }
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          "Could not activate the free plan. Please try again.";
        toast.error(msg, {
          autoClose: 15000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            maxWidth: "90%",
            width: "600px",
            wordWrap: "break-word",
            whiteSpace: "normal",
          },
        });
        console.error(error);
      } finally {
        setActivatingFree(false);
      }
      return;
    }
     if (hasAuthenticatedSubscription) {
        toast.warning(
          "You already have an upgrade in progress. Please wait for it to complete before requesting another upgrade.",
          {
            autoClose: 15000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
              maxWidth: "90%",
              width: "600px",
              wordWrap: "break-word",
              whiteSpace: "normal",
            },
          }
        );
        return;
      }
    if (subscriptionStatus === "active" || subscriptionStatus === "completed") {
      // Check if user already has an authenticated (pending upgrade) subscription
      // Same plan selected - allow dialog (renewal)
      if (selectedPlan?.id === "free" && freePlanUsed) {
        return;
      }
      setOpenDialog(true);
    } else {
      navigate("/checkoutPage", { state: { plan: selectedPlan } });
    }
  };

  const isUpgradeMode =
    !!userId &&
    (subscriptionStatus === "active" || subscriptionStatus === "completed");
  const upgradeDisabledForFreeUsed =
    isUpgradeMode && selectedPlan?.id === "free" && freePlanUsed;

  const handleUpgradeConfirm = () => {
    setOpenDialog(false);
    let startAt = null;
    if (currentPlanId !== "free") {
      startAt = Math.floor(new Date(subscriptionEndDate).getTime() / 1000);
    }
    const checkoutState = { plan: selectedPlan };
    if (startAt != null) checkoutState.start_at = startAt;
    navigate("/checkoutPage", { state: checkoutState });
  };

  return (
    <Box sx={{ p: 4, textAlign: "center", position:"relative" }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">Choose Your Subscription Plan</Typography>
      <br/>
      {isPlanStillActive(subscriptionEndDate, subscriptionStatus) && (
        <Typography variant="body2" sx={{ position:"absolute", mb: 1, fontWeight: "bold", top:50, right:40  }} color="primary" >  Your current plan is expiring on {formatValidUntilDate(subscriptionEndDate)} </Typography>
      )}
      <Grid container spacing={3} justifyContent="center">
        {plans?.map((plan) => {
          const freeDisabled = plan.id === "free" && freePlanUsed;
          return (
          <Grid size={{ xs:12, md:4 }} key={plan.id}>
            <Card
              onClick={() => {
                if (freeDisabled) return;
                setSelectedPlan(plan);
              }}
              className={`plan-card ${selectedPlan?.id === plan.id ? "active" : "inactive"} ${freeDisabled ? "disabled" : ""}`}
            >
              <CardContent sx={{ textAlign: "center", flexGrow: 1, position: "relative" ,color: selectedPlan?.id === plan.id ? "#ffffff" : "#2E2E2E", }}>
                  {currentPlanId === plan.id && (
                    <Box className="ribbon"> ACTIVE </Box>
                  )}
                <Box sx={{py:2}}>
                   { plan.price !== 0 && (
                      <Box component='img' src={PremiumIcon} alt='premiumicon' width={20} height={20} sx={{ position: "absolute", top: 12, right: 12 }} />
                    )
                  }
                <Typography variant="h3" sx={{ mt: 1 }} >{plan.name}</Typography>
                <Typography variant="subtitle1">{plan.label}</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: selectedPlan?.id === plan.id ? "#ffffff" : "#2E2E2E"}} >{plan.currency} {plan.price}</Typography>
                <Typography variant="subtitle1">{plan.duration}</Typography>
                </Box>
                <Box sx={{ mt: 2, textAlign: "left" }}>
                  <Divider sx={{mb:2,borderColor:"#ffffff !important"}}/>
                  <Typography fontWeight="bold" sx={{ mb: 1 ,color: selectedPlan?.id === plan.id ? "#ffffff" : "#2E2E2E",}} >{plan.featuresLabel}</Typography>
                  {plan.features?.slice(0, 4).map((feature, i) => (
                    <Typography
                      key={i}
                      variant="body2"
                      sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mb: 0.5 , color: selectedPlan?.id === plan.id ? "#ffffff" : "#2E2E2E",}}
                    >
                      ✔ {feature}
                    </Typography>
                  ))}
                    {freeDisabled && (
                     <Box className="ribbon" sx={{background:"#d32f2f"}}> NOT AVAILABLE </Box>
                    )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
        })}
      </Grid>

      <Box sx={{ m : 4 }}>
        <Button
          variant="contained"
          size="large"
          disabled={
            !selectedPlan ||
            (selectedPlan?.id === "free" && freePlanUsed) ||
            activatingFree ||
            upgradeDisabledForFreeUsed
          }
          onClick={handleNext}
        >
          {activatingFree ? (
            <CircularProgress size={24} color="inherit" />
          ) : !userId ? (
            "Login/Signup to Subscribe"
          ) : subscriptionStatus === "active" || subscriptionStatus === "completed" ? (
            "Upgrade"
          ) : (
            "Subscribe"
          )}
        </Button>
      </Box>

      <LoginPopup open={openLogin} onClose={() => setOpenLogin(false)} />

      <Dialog
        open={freeSuccessOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setFreeSuccessOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.65)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ px: 3, pt: 4, pb: 3, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            {[
              { l: "6%", t: "10%", w: 5, h: 12, r: 15, c: "#E53935" },
              { l: "88%", t: "14%", w: 4, h: 10, r: -22, c: "#FDD835" },
              { l: "12%", t: "22%", w: 6, h: 6, r: 0, c: "#43A047", round: true },
              { l: "82%", t: "28%", w: 5, h: 14, r: 35, c: "#8E24AA" },
              { l: "18%", t: "38%", w: 4, h: 4, r: 0, c: "#1E88E5", round: true },
              { l: "78%", t: "42%", w: 6, h: 11, r: -12, c: "#E53935" },
              { l: "10%", t: "52%", w: 5, h: 13, r: 28, c: "#43A047" },
              { l: "90%", t: "55%", w: 4, h: 8, r: -35, c: "#FDD835" },
              { l: "22%", t: "62%", w: 6, h: 6, r: 0, c: "#8E24AA", round: true },
              { l: "72%", t: "68%", w: 5, h: 12, r: -8, c: "#1E88E5" },
              { l: "8%", t: "72%", w: 4, h: 10, r: 20, c: "#FDD835" },
              { l: "85%", t: "78%", w: 5, h: 5, r: 0, c: "#E53935", round: true },
              { l: "48%", t: "8%", w: 5, h: 11, r: 45, c: "#43A047" },
              { l: "52%", t: "18%", w: 4, h: 9, r: -20, c: "#8E24AA" },
            ].map((p, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: p.l,
                  top: p.t,
                  width: p.w,
                  height: p.h,
                  bgcolor: p.c,
                  borderRadius: p.round ? "50%" : 0.5,
                  transform: `rotate(${p.r}deg)`,
                  opacity: 0.9,
                }}
              />
            ))}
          </Box>

          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              background: "linear-gradient(145deg, #FFF8E1 0%, #FFC107 42%, #F57F17 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 28px rgba(255, 152, 0, 0.4)",
              mx: "auto",
              mb: 2,
              position: "relative",
            }}
          >
            <Check sx={{ fontSize: 48, color: "#fff", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))" }} />
          </Box>

          <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 1.5, px: 1 }}>
            Free plan activated
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, maxWidth: 320, mx: "auto" }}>
            You have opted a Free subscription plan. Valid till {freeValidTill}.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => {
              setFreeSuccessOpen(false);
              navigate("/schematicEditor");
            }}
            sx={{ borderRadius: 999, py: 1.25, textTransform: "none", fontWeight: 600 }}
          >
            Go to Schematic Editor
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Upgrade Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Your current plan is expiring on{" "}
            <b>
              {formatValidUntilDate(subscriptionEndDate) || "N/A"}
            </b>
            . <br />
            Do you want to upgrade this plan?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>No</Button>
          <Button variant="contained" onClick={handleUpgradeConfirm}> Yes </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
